import { useState, useEffect } from 'react';
import { getValidToken } from '../utils/auth';
import { optionTradingViewUrl as tradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

// Score → color: red (0) → yellow (50) → green (100)
function scoreColor(score) {
  if (score >= 70) return '#22c55e';
  if (score >= 50) return '#eab308';
  if (score >= 35) return '#f97316';
  return '#ef4444';
}

function MomentumBadge({ momentum }) {
  if (momentum > 0) return <span className="text-xs font-semibold" style={{ color: '#22c55e' }}>▲ Bullish</span>;
  if (momentum < 0) return <span className="text-xs font-semibold" style={{ color: '#ef4444' }}>▼ Bearish</span>;
  return <span className="text-xs font-semibold text-gray-500">→ Flat</span>;
}

// Sortable table columns: key = payload field, numeric unless noted.
const COLUMNS = [
  { key: 'score',      label: 'Score',  align: 'left'  },
  { key: 'type',       label: 'Type',   align: 'left'  },
  { key: 'strike',     label: 'Strike', align: 'right' },
  { key: 'dte',        label: 'DTE',    align: 'right' },
  { key: 'buy',        label: 'Buy',    align: 'right' },
  { key: 'tp',         label: 'TP',     align: 'right' },
  { key: 'sl',         label: 'SL',     align: 'right' },
  { key: 'delta',      label: 'Δ',      align: 'right' },
  { key: 'iv',         label: 'IV',     align: 'right' },
  { key: 'spread_pct', label: 'Sprd',   align: 'right' },
  { key: 'volume',     label: 'Vol',    align: 'right' },
  { key: 'oi',         label: 'OI',     align: 'right' },
  { key: 'day_high',   label: 'DayHi',  align: 'right' },
  { key: 'revival',    label: 'Rev×',   align: 'right' },
];

// Ranked list of the best cheap ($0.50–$2.00) 0-1 DTE SPX contracts.
// Backend computes IV + greeks (Black-Scholes) and a composite score from
// gamma/delta per dollar, liquidity, reachability, theta burn, revival,
// pivot confluence and volume acceleration. Contracts enter at score ≥60 and
// stay listed until the smoothed score holds below 55 for 15s (hysteresis),
// so the list is stable enough to act on. Refreshes every 5 s.
export default function BestContractsWidget({ onContractSelect }) {
  const [data, setData] = useState(null);
  const [connStatus, setConnStatus] = useState('connecting');
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [sortCol, setSortCol] = useState('score');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let mounted = true;
    async function pollOnce() {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/spx-best`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await res.json();
        if (mounted) { setData(d); setConnStatus('live'); }
      } catch {
        if (mounted) setConnStatus('reconnecting');
      }
    }
    pollOnce();
    const timer = setInterval(pollOnce, 5000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  const handleRowClick = (c) => {
    setSelectedSymbol(c.symbol);
    if (onContractSelect) {
      onContractSelect({ symbol: c.symbol, price: c.buy, type: c.type, strike: c.strike });
    }
  };

  const handleSort = (col) => {
    if (col === sortCol) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const contracts = [...(data?.contracts || [])].sort((a, b) => {
    const va = a[sortCol], vb = b[sortCol];
    const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const emEntries = data ? Object.entries(data.expected_moves || {}) : [];

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">

      {/* Header */}
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          SPX Best Cheap Contracts
        </span>
        <span className="text-xs text-gray-500">$0.50 – $2.00 · 0-1 DTE · score ≥ 60</span>

        {data && <MomentumBadge momentum={data.momentum} />}

        {data && data.vix_trend !== 0 && (
          <span className="text-xs font-semibold"
            title="SPX ATM implied-volatility trend (VIX proxy, last 15 min) — feeds 10% of the contract score"
            style={{ color: data.vix_trend > 0 ? '#ef4444' : '#22c55e' }}>
            {data.vix_trend > 0 ? 'VIX ↑ favors Puts' : 'VIX ↓ favors Calls'}
          </span>
        )}

        {/* News badge removed — the SPY/SPX News widget above shows all
            classified headlines. Sentiment is still recorded per contract
            in the history table below. */}

        <div className="flex-1" />

        {data?.spot > 0 && (
          <span className="text-xs font-mono text-gray-300">SPX ≈ {data.spot.toFixed(2)}</span>
        )}
        {data?.futures_gap_set && (
          <span className="text-xs font-semibold font-mono"
            title="Live SPY-proxy vs prior session close — premarket this is the S&P futures gap"
            style={{ color: data.futures_gap_pct > 0.05 ? '#22c55e' : data.futures_gap_pct < -0.05 ? '#ef4444' : '#9ca3af' }}>
            Fut {data.futures_gap_pct >= 0 ? '+' : ''}{data.futures_gap_pct.toFixed(2)}%
          </span>
        )}
        {data && !data.market_open ? (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#eab308' }}>
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#eab308' }} />
            Market Closed · indicative quotes
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: connStatus === 'live' ? '#22c55e' : '#eab308' }} />
            {connStatus === 'live' ? 'Live · 5s' : connStatus}
          </span>
        )}
      </div>

      {/* Expected move per expiry */}
      {emEntries.length > 0 && (
        <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
          {emEntries.map(([exp, em]) => (
            <span key={exp}>
              {exp} <span className="text-gray-400">EM ±{em.toFixed(0)} pts</span>
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              <th className="text-left py-2 pr-2 font-medium">#</th>
              {COLUMNS.map(col => (
                <th key={col.key}
                  onClick={() => handleSort(col.key)}
                  title={col.key === 'revival'
                    ? 'Day-high ÷ current price — fallen-angel signal (click to sort)'
                    : 'Click to sort'}
                  className={`text-${col.align} py-2 pr-2 font-medium cursor-pointer select-none`}
                  style={col.key === sortCol ? { color: '#60a5fa' } : undefined}>
                  {col.label}{col.key === sortCol ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contracts.map((c, i) => {
              const isCall = c.type === 'C';
              const typeColor = isCall ? '#22c55e' : '#ef4444';
              const selected = c.symbol === selectedSymbol;
              return (
                <tr key={c.symbol}
                  onClick={() => handleRowClick(c)}
                  className="border-b cursor-pointer"
                  style={{
                    borderColor: '#1f2937',
                    backgroundColor: selected ? '#1e3a5f44' : 'transparent',
                  }}>
                  <td className="py-1.5 pr-2 text-gray-500">{i + 1}</td>
                  <td className="py-1.5 pr-2">
                    <span className="font-bold font-mono" style={{ color: scoreColor(c.score) }}>
                      {c.score.toFixed(1)}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: typeColor + '22', color: typeColor }}>
                      {isCall ? 'CALL' : 'PUT'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono">
                    <a href={tradingViewUrl(c.symbol)}
                      target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Open on TradingView"
                      style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {c.strike.toFixed(0)}
                    </a>
                  </td>
                  <td className="py-1.5 pr-2 text-right text-gray-400">{c.dte}</td>
                  <td className="py-1.5 pr-2 text-right font-mono font-bold text-gray-100">${c.buy.toFixed(2)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono" style={{ color: '#22c55e' }}>${c.tp.toFixed(2)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono" style={{ color: '#ef4444' }}>${c.sl.toFixed(2)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-300">{c.delta.toFixed(3)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{(c.iv * 100).toFixed(0)}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{c.spread_pct.toFixed(0)}%</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-300">{c.volume.toLocaleString()}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">{c.oi.toLocaleString()}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-gray-400">${c.day_high.toFixed(2)}</td>
                  <td className="py-1.5       text-right font-mono"
                    style={{ color: c.revival >= 3 ? '#eab308' : '#6b7280' }}>
                    {c.revival.toFixed(1)}
                  </td>
                </tr>
              );
            })}
            {contracts.length === 0 && (
              <tr>
                <td colSpan={15} className="py-6 text-center text-gray-600">
                  {connStatus === 'connecting'
                    ? 'Loading…'
                    : 'No contracts with score ≥ 60 right now (price $0.50–$2.00, bid ≥ $0.20, spread ≤ 40%, volume ≥ 100, within 2× expected move)'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-xs text-gray-600">
        Ranked by gamma/delta per dollar, liquidity, reachability, theta burn, revival,
        pivot confluence and volume acceleration. Contracts enter at score ≥ 60 and leave
        after holding below 55 for 15s — the list is stable, only scores update.
        TP = delta-gamma repricing at ½ expected move · SL = 1-hour theta decay (max −50%).
        Not a prediction — OTM contracts can expire worthless.
      </div>
    </div>
  );
}
